import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogService, InventoryReservation, PickWave } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-pick-waves',
  imports: [
    AinPageComponent, AinStateComponent, InventoryPageHintComponent, ButtonModule, TableModule,
    RouterLink, AinCanAccessDirective
  ],
  template: `
    <ain-page [title]="t('catalog.pickWaves.title')" [description]="t('catalog.pickWaves.description')">
      <ain-inventory-page-hint pageKey="pickWaves" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
      } @else {
        <div class="toolbar">
          <a pButton routerLink="/inventory/reservations" severity="secondary" [label]="t('catalog.pickWaves.createFromReservations')"></a>
          <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="reload()"></button>
        </div>

        @if (waves().length === 0) {
          <ain-state type="empty" [title]="t('catalog.pickWaves.empty')" />
        } @else {
          <p-table [value]="waves()" dataKey="id" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.pickWaves.referenceNumber') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('catalog.pickWaves.reservationCount') }}</th>
                <th>{{ t('catalog.pickWaves.sourceLabels') }}</th>
                <th>{{ t('catalog.inventory.occurredAt') }}</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><a [routerLink]="['/inventory/pick-waves', row.id]" class="link">{{ row.referenceNumber }}</a></td>
                <td>{{ row.status }}</td>
                <td>{{ row.reservationIds.length }}</td>
                <td>{{ sourceLabels(row) }}</td>
                <td>{{ row.createdAtUtc }}</td>
                <td>
                  @if (row.status === 'Draft') {
                    <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.pickWaves.release')" (click)="release(row)" [disabled]="busy()"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .error { color: #b91c1c; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryPickWavesComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly waves = signal<PickWave[]>([]);
  readonly reservations = signal<InventoryReservation[]>([]);
  t = (k: string) => this.localization.translate(k);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload(false)
    );
    this.reload();
  }

  sourceLabels(wave: PickWave): string {
    const refs = wave.reservationIds
      .map(id => this.reservations().find(r => r.id === id))
      .filter((r): r is InventoryReservation => !!r)
      .map(r => `${r.sourceType}: ${r.sourceReference}`);
    return refs.length ? refs.join('; ') : '-';
  }

  release(wave: PickWave): void {
    this.busy.set(true);
    this.service.releasePickWave(wave.id).subscribe({
      next: () => {
        this.busy.set(false);
        void this.router.navigate(['/inventory/pick-waves', wave.id]);
      },
      error: () => {
        this.error.set(this.t('catalog.pickWaves.releaseFailed'));
        this.busy.set(false);
      }
    });
  }

  reload(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.error.set('');
    this.service.listPickWaves().subscribe({
      next: waves => {
        this.waves.set(waves);
        this.service.listInventoryReservations().subscribe({
          next: reservations => {
            this.reservations.set(reservations);
            this.permissionDenied.set(false);
            this.loading.set(false);
          },
          error: () => this.loading.set(false)
        });
      },
      error: err => {
        this.waves.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('catalog.pickWaves.loadFailed'));
        this.loading.set(false);
      }
    });
  }
}
